import React, {CSSProperties, FC, memo, useState} from "react";
import {ApolloError} from "@apollo/client";
import {Alert, Space} from "antd";
import {InfoCircleOutlined} from "@ant-design/icons";
import {red} from "@ant-design/colors";
import Button from "../../../../views/Button/Button";
import {KnownError} from "../../../../errors/handleErrors";
import i18n from "../../../../services/i18n";

type LessonBookingErrorsProps = {
    error?: Error | ApolloError;
};

export const knownErrors: Array<KnownError> = [
    {
        id: 1,
        key: "Booking already exists",
        title: "Buchung existiert bereits",
        msg: "Eine Buchung mit diesen Daten existiert bereits.",
    },
    {
        id: 2,
        key: "courseId is required",
        title: "Kurs ist erforderlich",
        msg: "Bitte wähle einen Kurs um eine Buchung zu erstellen.",
    },
    {
        id: 3,
        key: "Place reservation is not possible because there are no free slots",
        title: "Platzreservierung ist nicht möglich, weil keine freien Plätze verfügbar sind",
        msg: "Wähle einen Teilnehmer aus, der noch nicht an diesem Kurs teilgenommen hat.",
    },
    {
        id: 4,
        key: "Attendee is already booked in all lessons",
        title: "Teilnehmer ist bereits in allen Kurseinheiten gebucht",
        msg: "Es gibt keine Kurseinheit die für diesen Teilnehmer noch buchbar ist.",
    },
    {
        id: 5,
        key: "Attendee is already booked in selected lessons",
        title: "Teilnehmer ist bereits in ausgewählten Kurseinheiten gebucht",
        msg: "Teilnehmer können nur einmal pro Kurseinheit gebucht werden.",
    },
    {
        id: 6,
        key: "Not saved changes",
        title: `${i18n.containers.bookings.BookingSteps.staffNotice()} nicht geändert`,
        msg: `Bitte übernehme oder verwerfe die Änderungen.`,
    },
    // #676 (E3): Server-Meldungen für Buchungen, auf denen bereits Geld liegt.
    // Die Schlüssel sind Fragmente der Server-Texte (lessonBooking.lockedRatesError.ts
    // und der #728-Abbruch in lessonBooking.service.ts).
    {
        id: 7,
        key: "Bearbeitung der Raten gesperrt",
        title: "Rate teilbezahlt, Bearbeitung der Raten gesperrt",
        msg: "Mindestens eine Rate dieser Buchung ist bereits bezahlt, teilbezahlt oder erstattet und kann im Editor nicht ersetzt werden. Bitte stattdessen „Buchung korrigieren“ verwenden.",
    },
    // #729: Der pauschale Überzahlungs-Abbruch (vorher id 8) ist entfallen — der Editor
    // bucht den Überschuss selbst als Bucher-Guthaben (siehe OverpaymentCreditHint).
    // Nur diese zwei Sonderfälle bleiben ein Abbruch (editOverpaymentCredit.ts).
    {
        id: 11,
        key: "eine teilbezahlte Rate ist eine Online-Zahlung (Mollie)",
        title: "Überzahlung mit teilbezahlter Online-Rate",
        msg: "Für diese Buchung wurde mehr bezahlt, als der neue Preis beträgt, und eine teilbezahlte Rate ist eine Online-Zahlung (Mollie). Der Überschuss kann im Editor nicht als Guthaben gebucht werden, weil der Mollie-Zahlungslink dabei entwertet werden muss. Bitte stattdessen „Buchung korrigieren“ verwenden.",
    },
    {
        id: 12,
        key: "eine teilbezahlte Rate wurde bereits (teilweise) erstattet",
        title: "Überzahlung mit erstatteter Teilrate",
        msg: "Für diese Buchung wurde mehr bezahlt, als der neue Preis beträgt, und eine teilbezahlte Rate wurde bereits (teilweise) erstattet. Der verbleibende Geldeingang lässt sich nicht eindeutig als Guthaben verbuchen. Bitte stattdessen „Buchung korrigieren“ verwenden.",
    },
    // #1008: Mitarbeiter-Sperre für Bezahlmethoden (Server-Code PAYMENT_METHOD_NOT_RELEASED).
    {
        id: 13,
        key: "ist in den App-Einstellungen nicht freigegeben",
        title: "Bezahlmethode nicht freigegeben",
        msg: "Diese Bezahlmethode ist in den App-Einstellungen für Mitarbeiter gesperrt. Bitte eine freigegebene Bezahlmethode wählen oder die Freigabe in den Einstellungen ändern.",
    },
    {
        id: 10,
        key: "Diese Rate hat eine offene Mollie-Zahlung",
        title: "Offene Online-Zahlung auf einer Rate",
        msg: "Eine Rate dieser Buchung hat eine noch offene Mollie-Zahlung. Bitte warte, bis die Zahlung abgeschlossen ist, oder storniere die Mollie-Zahlung zuerst.",
    },
    {
        id: 9,
        key: "den neuen Zahlplan vollständig belegen",
        title: "Bezahlte Raten belegen den neuen Zahlplan",
        msg: "Diese Buchung hat bezahlte oder teilbezahlte Raten, die den neuen Zahlplan vollständig belegen. Der Restbetrag ließe sich nicht unterbringen. Bitte stattdessen „Buchung korrigieren“ verwenden.",
    },
];

// Extrahiert eine Error-Message aus ApolloError — deckt auch den Fall ab, dass
// die Fehler in `networkError.result.errors` statt in `graphQLErrors` stehen
// (z.B. bei HTTP 400 durch GraphQL-Schema-Validierung vor Resolver-Aufruf).
const extractApolloErrorMessage = (error: ApolloError): string => {
    if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        return error.graphQLErrors[0].message || "";
    }

    const networkError = error.networkError as {result?: {errors?: Array<{message?: string}>}} | null | undefined;
    const networkErrors = networkError?.result?.errors;
    if (networkErrors && networkErrors.length > 0 && networkErrors[0].message) {
        return networkErrors[0].message;
    }

    return error.message || "";
};

const errorStyle: CSSProperties = {
    padding: "0.2rem 1rem",
    backgroundColor: red[2],
    color: red[7],
    fontWeight: 450,
};

/**
 * Fallback für unbekannte Fehler. #676: gilt auch für einfache Error — die landeten
 * vorher auf `return null` und waren damit unsichtbar, obwohl `state.error` gesetzt
 * war (typischer Fall: eine fehlgeschlagene Vorschau im Bearbeiten-Modus).
 *
 * Bewusst auf Modulebene: eine innerhalb der Render-Funktion definierte Komponente
 * bekommt bei jedem Render eine neue Identität (Remount, „Mehr Info" klappt sofort
 * wieder zu). Und im Scope einer anderen Funktion wäre sie hier gar nicht sichtbar —
 * genau das war der ReferenceError, den der Client-Build nicht bemerkt (keine
 * Typprüfung im CI).
 */
/**
 * Identität des Fallback-Alerts. Der Alert ist `closable`, und AntD 4 hält den
 * Schliess-Zustand in eigenem State, den es bei einem Prop-Wechsel NICHT zurücksetzt.
 * Ohne einen Key aus der Meldung bliebe nach einem Klick auf das X auch jeder
 * FOLGEFEHLER unsichtbar — also genau der stille Fehlschlag, den diese Komponente
 * beseitigen soll.
 *
 * Bis die Komponente auf Modulebene gezogen wurde, hat das ausgerechnet ein
 * Antipattern verdeckt: die bei jedem Render neu erzeugte Identität führte zu einem
 * Remount, der den Alert nebenbei zurücksetzte.
 *
 * Derselbe Fehler bleibt bewusst geschlossen — wer ihn weggeklickt hat, will ihn
 * nicht bei jedem Tastendruck erneut sehen.
 */
const fallbackKey = (error: Error): string => `${error.name}:${error.message}`;

const RenderFallback: FC<{error: Error}> = ({error}) => {
    const [showDescription, setShowDescription] = useState(false);

    return (
        <Alert
            style={errorStyle}
            message={<div style={{color: errorStyle.color}}>{"Ein Fehler ist aufgetreten"}</div>}
            description={showDescription ? error.message : null}
            type="error"
            showIcon
            closable
            action={
                <Button type="text" size="small" onClick={() => setShowDescription(!showDescription)} style={{color: errorStyle.color, marginRight: "-0.5rem"}}>
                    <Space>
                        <InfoCircleOutlined />
                        <div>{"Mehr Info"}</div>
                    </Space>
                </Button>
            }
        />
    );
};

export const getKnownErrorMessage = (error?: Error | ApolloError): string | undefined => {
    if (!error) {
        return undefined;
    }

    if (error instanceof ApolloError) {
        const errorMessage = extractApolloErrorMessage(error);
        const knownError = knownErrors.find((known) => errorMessage.includes(known.key));

        return knownError?.msg ?? "Ein Fehler ist aufgetreten";
    }

    if (error instanceof Error) {
        const knownError = knownErrors.find((known) => error.message.includes(known.key));

        return knownError?.msg ?? "Ein Fehler ist aufgetreten";
    }

    return "Ein Fehler ist aufgetreten";
};

const LessonBookingErrors: FC<LessonBookingErrorsProps> = ({error}) => {
    if (error instanceof ApolloError) {
        const errorMessage = extractApolloErrorMessage(error);
        const knownError = knownErrors.find((known) => errorMessage.includes(known.key));

        const RenderError = ({knownError}: {knownError: KnownError}) => {
            const [showDescription, setShowDescription] = useState(false);

            return (
                <Alert
                    style={errorStyle}
                    message={<div style={{color: errorStyle.color}}>{knownError.title}</div>}
                    description={showDescription ? knownError.msg : null}
                    type="error"
                    showIcon
                    action={
                        <Button
                            type="text"
                            size="small"
                            onClick={() => {
                                setShowDescription(!showDescription);
                            }}
                            style={{color: errorStyle.color, marginRight: "-0.5rem"}}
                        >
                            <Space>
                                <InfoCircleOutlined />
                                <div>{"Mehr Info"}</div>
                            </Space>
                        </Button>
                    }
                />
            );
        };

        if (knownError) {
            return <RenderError knownError={knownError} />;
        }

        return <RenderFallback key={fallbackKey(error)} error={error} />;
    }

    if (error) {
        return <RenderFallback key={fallbackKey(error)} error={error} />;
    }

    return null;
};

export default memo(LessonBookingErrors);
